import { PkButton, PkIcon, PkPopover } from '@uefadigital/panenka-react';
import dayjs from 'dayjs';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import type { OnArgs, TileArgs } from 'react-calendar';
import Calendar from 'react-calendar';

import { useLivescoresTabs } from '../../hooks/tabs';
import { useLivescoresOptions } from '../../store';
import { scrollToTab } from '../../utils/scroll';
import { track } from '../../utils/tracking';

import { useWindowSize } from '@hooks/useWindowSize';
import { useTranslateFn } from '@shared/Translate';
import { culture } from '@utilities/browser';
import { dateToString } from '@utilities/dateTime';

import * as styles from './styles.module.scss';

import 'react-calendar/dist/Calendar.css';

const LabelIcon = ({ dir }: { dir: 'left' | 'right' }) => (
  <>
    <PkIcon className={styles.arrow} name={`navigation-chevron-${dir}-circle`} color='interaction' size={24} />
    <PkIcon className={styles.arrowHover} name={`navigation-chevron-${dir}-xs`} color='interaction-compl' size={20} />
  </>
);

const DatePicker = () => {
  const t = useTranslateFn();
  const { isMobile } = useWindowSize();
  const { options } = useLivescoresOptions();
  const { selectedTab, tabs, loadPrevious, loadNext } = useLivescoresTabs();
  const [currentDate, setCurrentDate] = useState(null);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const popoverRef = useRef<HTMLPkPopoverElement>(null);

  const handlePopoverShow = () => {
    track('Open select date', 'uefa_calendar_open_select_date');
    setPopoverVisible(true);
  };
  const handlePopoverHide = () => setPopoverVisible(false);

  useEffect(() => {
    if (!selectedTab) return;

    const date = dayjs(selectedTab.date).toDate();
    setCurrentDate(date);
  }, [selectedTab]);

  const handleCalendarClick = (value: Date) => {
    track('Select date', 'uefa_calendar_select_date');
    const target = dateToString(value);

    popoverRef.current?.addEventListener(
      'pkAfterHide',
      () => {
        scrollToTab(target);
      },
      { once: true },
    );

    popoverRef.current?.hide();
  };

  const getDateDisabled = useCallback(
    ({ date, view }: TileArgs) => view !== 'year' && !tabs.some((d) => dayjs(d.date).isSame(date, 'd')),
    [tabs],
  );

  const handleActiveDateChange = ({ activeStartDate, view }: OnArgs) => {
    if (view !== 'month' || !tabs.length) return;

    if (dayjs(activeStartDate).isBefore(dayjs(tabs.at(0).date))) {
      loadPrevious();
    } else if (dayjs(activeStartDate).isAfter(dayjs(tabs.at(-1).date))) {
      loadNext();
    }
  };

  return (
    <PkPopover
      ref={popoverRef}
      className={styles.popover}
      onPkShow={handlePopoverShow}
      onPkHide={handlePopoverHide}
      stroke='shadow'
      noCloseButton
      enableOverlay
      placement='bottom-end'
      hideArrow
      pk-theme='light'
    >
      <PkButton
        className={styles.button}
        pk-theme={options.headerTheme}
        slot='trigger'
        appearance='outlined'
        width='auto'
      >
        {!isMobile && t('select_date')}
        <PkIcon className='pk-ml-sm--xs' name='support-calendar' size={24} />
      </PkButton>
      {!!selectedTab && popoverVisible && (
        <div className={styles.calendar}>
          <Calendar
            tileClassName={styles.calendarTile}
            locale={culture}
            value={currentDate}
            minDetail='year'
            tileDisabled={getDateDisabled}
            onViewChange={handleActiveDateChange}
            onActiveStartDateChange={handleActiveDateChange}
            onClickDay={handleCalendarClick}
            prevLabel={<LabelIcon dir='left' />}
            nextLabel={<LabelIcon dir='right' />}
            prev2Label={null}
            next2Label={null}
          />
        </div>
      )}
    </PkPopover>
  );
};

export default memo(DatePicker);
